import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import empManagementBg from "../assets/images/emp.jpg";

// Sliding sections data
const slidingSections = [
  {
    id: 1,
    title: "Onboarding & Recruitment",
    description: "Streamline hiring with automated workflows, candidate tracking, and digital onboarding.",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
      </svg>
    ),
    color: "from-blue-500 to-cyan-500"
  },
  {
    id: 2,
    title: "Attendance & Time Tracking",
    description: "Biometric integration, GPS tracking, and smart timesheets for accurate payroll.",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: "from-purple-500 to-pink-500"
  },
  {
    id: 3,
    title: "Payroll & Benefits",
    description: "Automated salary processing, tax calculations, and benefits administration.",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: "from-green-500 to-emerald-500"
  },
  {
    id: 4,
    title: "Performance Management",
    description: "Goal setting, performance reviews, and 360-degree feedback systems.",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    color: "from-orange-500 to-red-500"
  }
];

const HeroSection = () => {
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const autoPlayRef = useRef(null);
    const sectionRef = useRef(null);

    // Auto-slide functionality
    useEffect(() => {
        if (isAutoPlaying) {
            autoPlayRef.current = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % slidingSections.length);
            }, 4000);
        }
        return () => {
            if (autoPlayRef.current) clearInterval(autoPlayRef.current);
        };
    }, [isAutoPlaying]);

    // Pause auto-play on hover
    const handleMouseEnter = () => setIsAutoPlaying(false);
    const handleMouseLeave = () => setIsAutoPlaying(true);

    // Navigation handlers
    const goToSlide = (index) => {
        setCurrentSlide(index);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 5000);
    };

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % slidingSections.length);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 5000);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + slidingSections.length) % slidingSections.length);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 5000);
    };

    return (
        <section className="relative w-full overflow-hidden bg-gradient-to-br from-blue-100 via-white to-blue-100">
            <div className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-400 rounded-full blur-3xl"></div>
                </div>

                {/* Content Container */}
                <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        
                        {/* Left Column - Text Content */}
                        <div className="text-center lg:text-left">
                            {/* Badge */}
                            {/* <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-6">
                                <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
                                <span className="text-sm font-medium">Employee Management System</span>
                            </div> */}

                            {/* Main Heading */}
                            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6">
                                Manage Your
                                <span className="text-blue-600 block mt-2 relative">
                                    Workforce Efficiently
                                    <svg className="absolute -bottom-2 left-0 w-full h-2 text-blue-600" viewBox="0 0 200 8" fill="currentColor">
                                        <path d="M0,4 Q25,0 50,4 Q75,8 100,4 Q125,0 150,4 Q175,8 200,4" stroke="currentColor" fill="none" strokeWidth="2"/>
                                    </svg>
                                </span>
                            </h1>

                            {/* Description */}
                            <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                Streamline your HR operations with our comprehensive employee management solution. 
                                From onboarding to payroll, manage everything in one place with ease and precision.
                            </p>

                            {/* Features List */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                                <div className="flex items-center gap-2 group cursor-pointer">
                                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                    </div>
                                    <span className="text-gray-700">Centralized Database</span>
                                </div>
                                <div className="flex items-center gap-2 group cursor-pointer">
                                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                    </div>
                                    <span className="text-gray-700">Payroll Management</span>
                                </div>
                                <div className="flex items-center gap-2 group cursor-pointer">
                                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                    </div>
                                    <span className="text-gray-700">Attendance Tracking</span>
                                </div>
                                <div className="flex items-center gap-2 group cursor-pointer">
                                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                    </div>
                                    <span className="text-gray-700">Performance Reviews</span>
                                </div>
                            </div>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className="group px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                                >
                                    <span>Get Started Free</span>
                                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Right Column - Sliding Sections */}
                        <div 
                            className="relative hidden lg:block"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            ref={sectionRef}
                        >
                            {/* Main Sliding Container */}
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                {/* Background Image with overlay */}
                                <div className="relative">
                                    <img
                                        src={empManagementBg}
                                        alt="Employee Management Dashboard"
                                        className="w-full h-auto object-cover rounded-2xl transition-all duration-500"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-br from-gray-900/85 to-gray-800/80 rounded-2xl"></div>
                                </div>

                                {/* Sliding Content Overlay */}
                                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                                    {/* Top Space */}
                                    <div></div>

                                    {/* Sliding Content */}
                                    <div className="relative overflow-hidden min-h-[240px]">
                                        {slidingSections.map((section, idx) => (
                                            <div
                                                key={section.id}
                                                className={`absolute inset-0 transition-all duration-500 transform ${
                                                    idx === currentSlide
                                                        ? "translate-x-0 opacity-100"
                                                        : idx < currentSlide
                                                        ? "-translate-x-full opacity-80"
                                                        : "translate-x-full opacity-80"
                                                }`}
                                            >
                                                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-5 shadow-lg">
                                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${section.color} flex items-center justify-center text-white mb-3 shadow-lg`}>
                                                        {section.icon}
                                                    </div>
                                                    <h3 className="text-lg font-bold text-gray-800 mb-2">
                                                        {section.title}
                                                    </h3>
                                                    <p className="text-sm text-gray-600 leading-relaxed">
                                                        {section.description}
                                                    </p>
                                                    
                                                    {/* Learn More Link */}
                                                    <button className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 group">
                                                        Learn More
                                                        <svg className="w-3 h-3 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Carousel Indicator at Bottom */}
                                    <div className="mt-4 flex flex-col items-center gap-3">
                                        {/* Navigation Arrows */}
                                        <div className="flex justify-center gap-3">
                                            {/* <button
                                                onClick={prevSlide}
                                                className="w-8 h-8 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={nextSlide}
                                                className="w-8 h-8 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button> */}
                                        </div>

                                        {/* Slide Indicator Dots */}
                                        <div className="flex justify-center gap-2">
                                            {slidingSections.map((section, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => goToSlide(idx)}
                                                    className="group relative"
                                                >
                                                    <div
                                                        className={`transition-all duration-300 rounded-full ${
                                                            currentSlide === idx
                                                                ? `w-8 h-2 bg-gradient-to-r ${section.color}`
                                                                : "w-2 h-2 bg-white/40 hover:bg-white/60"
                                                        }`}
                                                    />
                                                    {/* Tooltip on hover */}
                                                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                                        {section.title.split(' & ')[0]}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>

                                        {/* Slide Counter */}
                                        <div className="text-white/60 text-xs font-mono">
                                            {String(currentSlide + 1).padStart(2, '0')} / {String(slidingSections.length).padStart(2, '0')}
                                        </div>
                                    </div>
                                </div>

                                {/* Floating Stats Cards */}
                                <div className="absolute top-4 -left-4 bg-white rounded-lg shadow-xl p-3 animate-float hidden xl:block">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Active Employees</p>
                                            <p className="text-xl font-bold text-gray-900">1,284</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="absolute bottom-4 -right-4 bg-white rounded-lg shadow-xl p-3 animate-float-delayed hidden xl:block">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Departments</p>
                                            <p className="text-xl font-bold text-gray-900">12</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Animations */}
            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                }
                
                @keyframes floatDelayed {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-15px); }
                }
                
                .animate-float {
                    animation: float 3s ease-in-out infinite;
                }
                
                .animate-float-delayed {
                    animation: floatDelayed 3.5s ease-in-out infinite;
                }
            `}</style>
        </section>
    );
};

export default HeroSection;